function scopeCss(cssText, scopeSelector) {
    let css = cssText.replace(/\/\*[\s\S]*?\*\//g, '');
    const rules = css.split('}');
    let scopedCss = '';
    
    rules.forEach(rule => {
        const parts = rule.split('{');
        if (parts.length === 2) {
            const selectors = parts[0].split(',');
            const body = parts[1];
            
            const scopedSelectors = selectors.map(sel => {
                const s = sel.trim();
                if (!s) return '';
                if (s === 'body' || s === 'html' || s === ':root') {
                    return scopeSelector;
                }
                if (s.startsWith('body ') || s.startsWith('html ')) {
                    return scopeSelector + s.substring(4);
                }
                return scopeSelector + ' ' + s;
            }).filter(Boolean);
            
            if (scopedSelectors.length > 0) {
                scopedCss += scopedSelectors.join(', ') + ' {' + body + '}\n';
            }
        }
    });
    
    return scopedCss;
}

function splitHtmlIntoPageDivs(htmlText) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlText, 'text/html');
    
    // Extract CSS styles and scope them to .pdf-page to prevent global leakage
    const styles = Array.from(doc.querySelectorAll('style'))
        .map(s => {
            const scoped = scopeCss(s.innerHTML, '.pdf-page');
            return `<style>${scoped}</style>`;
        })
        .join('\n');
    
    // Define standard A4 page styles (CSS for printing)
    const pageStyles = `
<style>
    @page {
        size: A4 portrait;
        margin: 20mm;
    }
    .pdf-page {
        box-sizing: border-box;
        background: #ffffff;
        width: 794px; /* Standard A4 width at 96 DPI */
        min-height: 1123px; /* Standard A4 height at 96 DPI */
        padding: 40px; /* Internal margin fallback */
        margin: 0 auto;
        page-break-after: always;
        position: relative;
    }
</style>`;

    // Find the table
    const table = doc.querySelector('table');
    if (!table) {
        const pageDiv = document.createElement('div');
        pageDiv.className = 'pdf-page';
        pageDiv.innerHTML = pageStyles + styles + doc.body.innerHTML;
        return [pageDiv];
    }
    
    // Extract header HTML (everything before the table)
    const bodyClone = doc.body.cloneNode(true);
    const clonedTable = bodyClone.querySelector('table');
    if (clonedTable) {
        let next = clonedTable;
        while (next) {
            const toRemove = next;
            next = next.nextSibling;
            toRemove.parentNode.removeChild(toRemove);
        }
    }
    const headerHtml = bodyClone.innerHTML;
    
    // Extract tbody/table rows, excluding header and footer rows
    const tfoot = table.querySelector('tfoot');
    const tfootRows = tfoot ? Array.from(tfoot.querySelectorAll('tr')) : [];
    
    let rows = Array.from(table.querySelectorAll('tr'));
    
    // Exclude tfoot rows from the main chunkable rows
    if (tfootRows.length > 0) {
        rows = rows.filter(r => !tfootRows.includes(r));
    }
    
    // Extract head row (the first tr in the table)
    const firstRow = table.querySelector('tr');
    let theadHtml = '';
    if (firstRow && firstRow.querySelector('th')) {
        theadHtml = `<thead>${firstRow.outerHTML}</thead>`;
        rows = rows.filter(r => r !== firstRow);
    }
    
    const tfootHtml = tfoot ? tfoot.outerHTML : '';
    
    // Preserve table attributes
    const tableAttrs = Array.from(table.attributes)
        .map(attr => `${attr.name}="${attr.value}"`)
        .join(' ');
        
    let firstPageSize = 20;
    let nextPageSize = 26;
    
    if (htmlText.toLowerCase().includes('price list')) {
        firstPageSize = 35;
        nextPageSize = 44;
    }
    
    const pageDivs = [];
    let rowIndex = 0;
    let pageNumber = 1;
    
    while (rowIndex < rows.length || rowIndex === 0) {
        const pageDiv = document.createElement('div');
        pageDiv.className = 'pdf-page';
        
        let contentHtml = pageStyles + styles;
        
        if (pageNumber === 1) {
            contentHtml += headerHtml;
        }
        
        const currentLimit = (pageNumber === 1) ? firstPageSize : nextPageSize;
        const chunkRows = rows.slice(rowIndex, rowIndex + currentLimit);
        rowIndex += currentLimit;
        
        if (chunkRows.length > 0) {
            let tableHtml = `<table ${tableAttrs}>`;
            if (theadHtml) {
                tableHtml += theadHtml;
            }
            tableHtml += '<tbody>';
            chunkRows.forEach(row => {
                tableHtml += row.outerHTML;
            });
            tableHtml += '</tbody>';
            if (rowIndex >= rows.length && tfootHtml) {
                tableHtml += tfootHtml;
            }
            tableHtml += '</table>';
            contentHtml += tableHtml;
        }
        
        contentHtml += `<div style="text-align: right; font-size: 8px; color: #777; margin-top: 15px; font-family: Arial, sans-serif;">Page ${pageNumber}</div>`;
        
        pageDiv.innerHTML = contentHtml;
        pageDivs.push(pageDiv);
        pageNumber++;
        
        if (rowIndex >= rows.length) {
            break;
        }
    }
    
    const totalPages = pageDivs.length;
    pageDivs.forEach((div, index) => {
        const footer = div.querySelector('div[style*="text-align: right"]');
        if (footer) {
            footer.innerText = `Page ${index + 1} of ${totalPages}`;
        }
    });
    
    return pageDivs;
}

function loadScripts(urls, callback) {
    let loadedCount = 0;
    if (urls.length === 0) {
        callback();
        return;
    }
    urls.forEach(url => {
        const script = document.createElement('script');
        script.src = url;
        script.onload = () => {
            loadedCount++;
            if (loadedCount === urls.length) {
                callback();
            }
        };
        script.onerror = () => {
            console.error('Failed to load script: ' + url);
            loadedCount++;
            if (loadedCount === urls.length) {
                callback();
            }
        };
        document.head.appendChild(script);
    });
}

export function generateAndShareBrowserPdf(htmlText, fileName, callback) {
    if (!htmlText) {
        if (typeof callback === 'function') callback();
        return;
    }

    // Intercept if running in FlutterApp/Flutter User Agent
    const userAgent = navigator.userAgent || '';
    if (userAgent.includes('FlutterApp') || userAgent.includes('Flutter')) {
        console.log('Detected Flutter environment, sending HTML content...');
        
        // Inject standard A4 @page margins before sending to Flutter's native webview
        const standardA4Styles = `
            <style>
                @page {
                    size: A4 portrait;
                    margin: 20mm; /* Standard A4 margin */
                }
                body {
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                    margin: 0; /* Let @page handle the margin */
                }
            </style>`;
        
        const flutterHtml = htmlText.includes('</head>') 
            ? htmlText.replace('</head>', standardA4Styles + '</head>')
            : standardA4Styles + htmlText;

        // 1. Try InAppWebView JavaScript handler (modern/preferred)
        if (window.flutter_inappwebview && typeof window.flutter_inappwebview.callHandler === 'function') {
            window.flutter_inappwebview.callHandler('printHtml', flutterHtml, fileName);
        }
        // 2. Try webview_flutter JavaScript Channel (standard webview_flutter)
        else if (window.FlutterPrintChannel && typeof window.FlutterPrintChannel.postMessage === 'function') {
            window.FlutterPrintChannel.postMessage(JSON.stringify({ html: flutterHtml, fileName: fileName }));
        }
        // 3. Fallback to general FlutterApp postMessage
        else if (window.FlutterApp && typeof window.FlutterApp.postMessage === 'function') {
            window.FlutterApp.postMessage(JSON.stringify({ html: flutterHtml, fileName: fileName }));
        }
        // 4. Fallback to Custom URL Scheme navigation (easiest for Flutter URL interception)
        else {
            const printUrl = 'print-html://show?fileName=' + encodeURIComponent(fileName) + '&html=' + encodeURIComponent(flutterHtml);
            window.location.href = printUrl;
        }

        if (typeof callback === 'function') callback();
        return;
    }

    // Append YYYYMMDD_HHMMSS timestamp to make filename unique and bypass cache
    let uniqueFileName = fileName || 'document.pdf';
    if (uniqueFileName.endsWith('.pdf')) {
        const baseName = uniqueFileName.slice(0, -4);
        const now = new Date();
        const timestamp = `${now.getFullYear()}${(now.getMonth()+1).toString().padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}_${now.getHours().toString().padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}${now.getSeconds().toString().padStart(2, '0')}`;
        uniqueFileName = `${baseName}_${timestamp}.pdf`;
    }

    const doGenerate = () => {
        const pageDivs = splitHtmlIntoPageDivs(htmlText);
        
        // Create hidden container in DOM to ensure stylesheets apply correctly
        const renderContainer = document.createElement('div');
        renderContainer.style.position = 'absolute';
        renderContainer.style.left = '0px';
        renderContainer.style.top = '0px';
        renderContainer.style.width = '794px';
        renderContainer.style.height = '0px';
        renderContainer.style.overflow = 'hidden';
        renderContainer.style.zIndex = '-9999';
        document.body.appendChild(renderContainer);
        pageDivs.forEach(div => renderContainer.appendChild(div));

        const handleBlob = (blob) => {
            var blobUrl = URL.createObjectURL(blob);
            var link = document.createElement('a');
            link.href = blobUrl;
            link.download = uniqueFileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(function() {
                URL.revokeObjectURL(blobUrl);
            }, 100);
            
            if (navigator.share && navigator.canShare) {
                try {
                    var file = new File([blob], uniqueFileName, { type: 'application/pdf' });
                    if (navigator.canShare({ files: [file] })) {
                        navigator.share({
                            title: 'Share PDF',
                            files: [file]
                        }).catch(console.error);
                    }
                } catch (shareErr) {
                    console.error('Share failed:', shareErr);
                }
            }
            if (typeof callback === 'function') callback();
        };

        const generatePdfWithScale = (scaleValue) => {
            return new Promise((resolve, reject) => {
                let jsPDFClass = window.jsPDF;
                if (!jsPDFClass && window.jspdf) {
                    jsPDFClass = window.jspdf.jsPDF;
                }
                if (!jsPDFClass) {
                    reject(new Error('jsPDF not found'));
                    return;
                }

                const pdf = new jsPDFClass({
                    orientation: 'portrait',
                    unit: 'in',
                    format: 'letter'
                });

                const html2canvasOpts = {
                    scale: scaleValue,
                    useCORS: true,
                    logging: false,
                    windowWidth: 794,
                    width: 794
                };

                let pageIdx = 0;

                const renderNextPage = () => {
                    if (pageIdx >= pageDivs.length) {
                        resolve(pdf.output('blob'));
                        return;
                    }

                    console.log(`Generating PDF page ${pageIdx + 1} of ${pageDivs.length}...`);

                    window.html2canvas(pageDivs[pageIdx], html2canvasOpts).then((canvas) => {
                        const imgData = canvas.toDataURL('image/jpeg', 0.95);
                        
                        const imgWidth = 7.5;
                        const imgHeight = (canvas.height * imgWidth) / canvas.width;
                        const margin = 0.5;

                        if (pageIdx > 0) {
                            pdf.addPage();
                        }
                        
                        pdf.addImage(imgData, 'JPEG', margin, margin, imgWidth, imgHeight);
                        pageIdx++;
                        renderNextPage();
                    }).catch((err) => {
                        reject(err);
                    });
                };

                renderNextPage();
            });
        };

        generatePdfWithScale(1.5).then((blob) => {
            if (document.body.contains(renderContainer)) {
                document.body.removeChild(renderContainer);
            }
            handleBlob(blob);
        }).catch((err) => {
            console.warn('PDF generation failed at scale 1.5, falling back to scale 1.2...', err);
            generatePdfWithScale(1.2).then((blob) => {
                if (document.body.contains(renderContainer)) {
                    document.body.removeChild(renderContainer);
                }
                handleBlob(blob);
            }).catch((err12) => {
                console.warn('PDF generation failed at scale 1.2, falling back to scale 1.0...', err12);
                generatePdfWithScale(1).then((blob) => {
                    if (document.body.contains(renderContainer)) {
                        document.body.removeChild(renderContainer);
                    }
                    handleBlob(blob);
                }).catch((finalErr) => {
                    if (document.body.contains(renderContainer)) {
                        document.body.removeChild(renderContainer);
                    }
                    console.error('PDF generation failed:', finalErr);
                    if (typeof callback === 'function') callback();
                });
            });
        });
    };

    const scriptsToLoad = [];
    if (typeof window !== 'undefined') {
        if (!window.html2canvas) {
            scriptsToLoad.push('https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js');
        }
        
        let hasJsPDF = window.jsPDF || (window.jspdf && window.jspdf.jsPDF);
        if (!hasJsPDF) {
            scriptsToLoad.push('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');
        }
    }

    const checkAndGenerate = () => {
        let hasJsPDF = window.jsPDF || (window.jspdf && window.jspdf.jsPDF);
        if (window.html2canvas && hasJsPDF) {
            doGenerate();
        } else {
            console.error('Required PDF libraries could not be loaded.');
            if (typeof callback === 'function') callback();
        }
    };

    if (scriptsToLoad.length > 0) {
        loadScripts(scriptsToLoad, checkAndGenerate);
    } else {
        checkAndGenerate();
    }
}
